
import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuthState";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { 
  Users, 
  UserPlus, 
  Mail, 
  Phone, 
  CheckCircle2, 
  Clock, 
  Search, 
  Send,
  Trash2,
  ArrowLeft
} from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Link, useLocation } from "wouter";

interface Friend {
  id: number;
  friendEmail?: string;
  friendPhone?: string;
  friendName?: string;
  friendUsername?: string;
  addedAt: string;
  isRegistered?: boolean;
  registeredUser?: {
    id: number;
    username: string;
    email: string;
    createdAt: string;
  } | null;
}

interface SearchUser {
  id: number;
  username: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
}

export default function MobileFriends() {
  const { toast } = useToast();
  const { user, loading } = useAuth();
  const [, setLocation] = useLocation();
  const [showSearchDialog, setShowSearchDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch friends list
  const { data: friendsData, isLoading: friendsLoading } = useQuery({
    queryKey: ['/api/friends'],
  });

  const friends = (friendsData as { friends: Friend[] })?.friends || [];

  // Search users query
  const { data: searchData, isLoading: isSearching, refetch: searchUsers } = useQuery({
    queryKey: [`/api/friends/search-users?query=${searchQuery}`],
    enabled: false,
  });

  const searchResults = (searchData as { users: SearchUser[] })?.users || [];

  // Add existing user mutation
  const addExistingUserMutation = useMutation({
    mutationFn: async (registeredUserId: number) => {
      return await apiRequest('POST', '/api/friends/add-existing', { 
        body: { registeredUserId } 
      });
    },
    onSuccess: () => {
      toast({
        title: "Friend Added!",
        description: "User has been added to your friend list.",
      });
      setShowSearchDialog(false);
      setSearchQuery('');
      queryClient.invalidateQueries({ queryKey: ['/api/friends'] });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to add friend",
        variant: "destructive",
      });
    },
  });

  // Remove friend mutation
  const removeFriendMutation = useMutation({
    mutationFn: async (friendId: number) => {
      return await apiRequest('DELETE', `/api/friends/${friendId}`);
    },
    onSuccess: () => {
      toast({
        title: "Friend Removed",
        description: "Friend has been removed from your list.",
      });
      queryClient.invalidateQueries({ queryKey: ['/api/friends'] });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to remove friend",
        variant: "destructive",
      });
    },
  });

  const handleSearch = () => {
    if (searchQuery.trim().length < 3) {
      toast({
        title: "Search Query Too Short",
        description: "Please enter at least 3 characters to search",
        variant: "destructive",
      });
      return;
    }
    searchUsers();
  };

  // Show loading while authentication is being determined
  if (loading || friendsLoading) {
    return <MobileFriendsSkeleton />;
  }

  // Friends requires authentication - redirect to auth if user is not authenticated
  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
        <div className="text-center">
          <Users className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Sign In Required
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Please sign in to manage your friends.
          </p>
          <Link href="/auth">
            <Button className="bg-blue-600 hover:bg-blue-700 text-white">
              Sign In
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-24 overflow-x-hidden">
      {/* Mobile Header */}
      <div className="text-white" style={{ background: 'linear-gradient(to right, #0e1b90, #1e2bb0)' }}>
        <div className="px-4 pt-12 pb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setLocation('/m/dashboard')}
                className="text-white hover:bg-white/20 p-2"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold">My Friends</h1>
                <p className="text-blue-100 text-sm">
                  Invite friends to competitions
                </p>
              </div>
            </div>
            <div className="bg-white/20 rounded-full p-3">
              <Users className="h-6 w-6" />
            </div>
          </div>
          
          {/* Stats */}
          <div className="bg-white/20 rounded-lg p-3">
            <div className="grid grid-cols-2 gap-4 text-center">
              <div>
                <div className="text-xl font-bold">{friends.length}</div>
                <div className="text-xs text-blue-100">Total Friends</div>
              </div>
              <div>
                <div className="text-xl font-bold">
                  {friends.filter(f => f.isRegistered).length}
                </div>
                <div className="text-xs text-blue-100">Registered</div>
              </div>
            </div>
          </div>

          {/* Add Friend Button */}
          <div className="mt-4">
            <Button 
              onClick={() => setShowSearchDialog(true)}
              className="w-full bg-white text-blue-600 hover:bg-gray-100"
              data-testid="button-add-friend"
            >
              <UserPlus className="h-4 w-4 mr-2" />
              Add Friend
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-4 py-6">
        {friends.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Friends Yet</h3>
              <p className="text-muted-foreground mb-6 text-sm">
                Add friends to invite them to competitions and challenges
              </p>
              <Button onClick={() => setShowSearchDialog(true)}>
                <UserPlus className="h-4 w-4 mr-2" />
                Add Your First Friend
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {friends.map((friend) => (
              <Card key={friend.id} data-testid={`card-friend-${friend.id}`}>
                <CardContent className="p-4">
                  <div className="space-y-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold">
                            {friend.friendName || friend.friendUsername || "Friend"}
                          </h3>
                          {friend.isRegistered ? (
                            <Badge variant="default" className="bg-green-500 hover:bg-green-600 text-xs" data-testid={`badge-status-registered-${friend.id}`}>
                              <CheckCircle2 className="h-3 w-3 mr-1" />
                              Registered
                            </Badge>
                          ) : (
                            <Badge variant="secondary" className="text-xs" data-testid={`badge-status-pending-${friend.id}`}>
                              <Clock className="h-3 w-3 mr-1" />
                              Pending
                            </Badge>
                          )}
                        </div>
                        {friend.friendUsername && (
                          <p className="text-sm text-muted-foreground">
                            @{friend.friendUsername}
                          </p>
                        )}
                        {friend.isRegistered && friend.registeredUser && (
                          <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                            Joined as @{friend.registeredUser.username}
                          </p>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeFriendMutation.mutate(friend.id)}
                        data-testid={`button-remove-friend-${friend.id}`}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                    <div className="space-y-1">
                      {friend.friendEmail && (
                        <div className="flex items-center text-sm text-muted-foreground">
                          <Mail className="h-3 w-3 mr-2" />
                          {friend.friendEmail}
                        </div>
                      )}
                      {friend.friendPhone && (
                        <div className="flex items-center text-sm text-muted-foreground">
                          <Phone className="h-3 w-3 mr-2" />
                          {friend.friendPhone}
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Add Friend Search Dialog */}
      <Dialog open={showSearchDialog} onOpenChange={setShowSearchDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Friend</DialogTitle>
            <DialogDescription>
              Search for existing users by phone number or email address
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex gap-2">
              <Input
                placeholder="27821234567 or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                data-testid="input-search-users"
              />
              <Button 
                onClick={handleSearch} 
                disabled={isSearching}
                data-testid="button-execute-search"
              >
                <Search className="h-4 w-4" />
              </Button>
            </div>

            {/* Search Results */}
            {searchResults.length > 0 && (
              <div className="space-y-2">
                <h3 className="font-semibold">Search Results</h3>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {searchResults.map((user) => (
                    <Card key={user.id} data-testid={`card-search-result-${user.id}`}>
                      <CardContent className="py-3">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <h4 className="font-semibold text-sm">
                              {user.firstName || user.lastName 
                                ? `${user.firstName || ''} ${user.lastName || ''}`.trim() 
                                : user.username}
                            </h4>
                            <p className="text-xs text-muted-foreground">
                              @{user.username}
                            </p>
                            <div className="flex gap-3 mt-1">
                              {user.email && (
                                <div className="flex items-center text-xs text-muted-foreground">
                                  <Mail className="h-3 w-3 mr-1" />
                                  {user.email}
                                </div>
                              )}
                              {user.phone && (
                                <div className="flex items-center text-xs text-muted-foreground">
                                  <Phone className="h-3 w-3 mr-1" />
                                  {user.phone}
                                </div>
                              )}
                            </div>
                          </div>
                          <Button
                            size="sm"
                            onClick={() => addExistingUserMutation.mutate(user.id)}
                            disabled={addExistingUserMutation.isPending}
                            data-testid={`button-add-user-${user.id}`}
                          >
                            <UserPlus className="h-4 w-4 mr-1" />
                            Add
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {searchData && searchResults.length === 0 && !isSearching && (
              <div className="text-center py-8 text-muted-foreground">
                <Users className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>No users found matching your search</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MobileFriendsSkeleton() {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-24 overflow-x-hidden">
      {/* Header Skeleton */}
      <div className="text-white px-4 pt-12 pb-6" style={{ background: 'linear-gradient(to right, #0e1b90, #1e2bb0)' }}>
        <div className="space-y-4">
          <Skeleton className="h-8 w-48 bg-white/20" />
          <Skeleton className="h-16 rounded-lg bg-white/20" />
          <Skeleton className="h-12 w-full bg-white/20" />
        </div>
      </div>

      {/* Content Skeleton */}
      <div className="px-4 py-6 space-y-4">
        {[1, 2, 3].map((i) => (
          <Card key={i}>
            <CardContent className="p-4">
              <div className="space-y-3">
                <div className="flex justify-between">
                  <Skeleton className="h-6 w-32" />
                  <Skeleton className="h-8 w-20" />
                </div>
                <Skeleton className="h-4 w-48" />
                <Skeleton className="h-4 w-40" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
